import React, { useEffect, useRef, useState } from 'react';
import { WarnIcon } from './icons';

// PRD-010 F-CC-2 / F-CC-3 — shared inline note panel.
//
// Replaces `window.prompt()` for the AgreementDetail accept-deliverable
// note (optional) and dispute-payment reason (required, ≥10 chars).
// Also reused by the Connections page revoke-invitation confirmation
// (F-PI-3) where it acts as an inline confirm with an optional reason.
//
// Variants:
//   • tone='neutral'  — accept-deliverable, revoke-invite (default).
//   • tone='danger'   — dispute-payment. Renders red border/warning icon.
//
// Notes:
//   • Default open=true; the parent toggles visibility. Pressing Escape
//     or clicking outside the panel calls onCancel.
//   • If `noteRequired` is true, the panel enforces `minNoteLength` (used
//     by the dispute flow which requires ≥10 chars).

export default function InlineNotePanel({
  title,
  description,
  primaryLabel = 'Submit',
  cancelLabel = 'Cancel',
  noteLabel = 'Note',
  notePlaceholder = '',
  noteRequired = false,
  minNoteLength = 0,
  tone = 'neutral',
  busy = false,
  onSubmit,
  onCancel,
}) {
  const [note, setNote] = useState('');
  const containerRef = useRef(null);
  const textareaRef = useRef(null);

  // Auto-focus the textarea when the panel mounts.
  useEffect(() => {
    if (textareaRef.current) textareaRef.current.focus();
  }, []);

  // Click-outside + Escape both close. The panel mounts on the click of
  // an action button — the parent button's own click event would
  // immediately re-trigger the close logic, so we register the listener
  // on the next tick.
  useEffect(() => {
    if (!onCancel) return;
    let mounted = false;
    const onMaybeClose = (e) => {
      if (!mounted) return;
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        onCancel();
      }
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onCancel();
    };
    const t = window.setTimeout(() => {
      mounted = true;
      document.addEventListener('mousedown', onMaybeClose);
    }, 0);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('mousedown', onMaybeClose);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onCancel]);

  const trimmed = (note || '').trim();
  const minOk = !noteRequired || trimmed.length >= minNoteLength;
  const submittable = minOk && !busy;

  function handleSubmit(e) {
    e.preventDefault();
    if (!submittable) return;
    onSubmit && onSubmit(trimmed || null);
  }

  const isDanger = tone === 'danger';

  return (
    <div
      ref={containerRef}
      className={`mt-2 mb-2 p-3 rounded-lg border-2 bg-white ${
        isDanger ? 'border-red-300 bg-red-50/30' : 'border-amber-200 bg-amber-50/30'
      }`}
    >
      <div className="flex items-start gap-2 mb-2">
        {isDanger && (
          <span aria-hidden className="text-red-600 mt-1"><WarnIcon /></span>
        )}
        <div className="flex-1 min-w-0">
          {title && (
            <div className={`text-sm font-semibold ${isDanger ? 'text-red-900' : 'text-amber-900'}`}>
              {title}
            </div>
          )}
          {description && (
            <p className={`text-xs mt-0.5 ${isDanger ? 'text-red-800' : 'text-amber-800'}`}>
              {description}
            </p>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-2">
        <label className="block">
          <span className="text-[11px] font-medium text-gray-600 uppercase tracking-wide">
            {noteLabel}{noteRequired ? ' (required)' : ' (optional)'}
          </span>
          <textarea
            ref={textareaRef}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={notePlaceholder}
            rows={3}
            className="mt-1 w-full text-sm border border-gray-200 rounded px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
          />
          {noteRequired && minNoteLength > 0 && (
            <span className={`text-[10px] mt-0.5 block ${minOk ? 'text-gray-600' : 'text-red-600'}`}>
              {`Minimum ${minNoteLength} characters · ${trimmed.length} so far`}
            </span>
          )}
        </label>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="text-xs px-3 py-1.5 rounded text-gray-700 hover:bg-gray-100 disabled:opacity-40"
          >
            {cancelLabel}
          </button>
          <button
            type="submit"
            disabled={!submittable}
            className={`text-xs px-3 py-1.5 rounded font-medium text-white shadow-sm disabled:opacity-40 ${
              isDanger
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-primary-600 hover:bg-primary-700'
            }`}
          >
            {busy ? 'Submitting…' : primaryLabel}
          </button>
        </div>
      </form>
    </div>
  );
}
